/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  Filler,
  TooltipItem
} from 'chart.js';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './line-chart.scss';
import { VIEW_TYPES } from '../../../core/types';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { DataGridContext } from '../../../forecast/detail/reports/data-grid/context/data-grid';

interface Props {
  ariaLabel?: string;
  legend?: 'top' | 'bottom' | 'left' | 'right';
  title?: string;
  labels: (string | string[])[];
  datasets: { label?: string; data: (number | null)[] }[];
  testId?: string;
  absolute?: boolean;
  markerIndex?: number;
  height?: number;
  showYesterdayMarker?: boolean;
  showMarker?: boolean;
  lineColorFn?: (ctx: { p0: PointElement; p0DataIndex: number }) => string;
  areaColorFn?: (ctx: { p0: PointElement; p0DataIndex: number }) => string;
  dateFormatter?: (date: string, value?: string) => string;
  currencyFormatter?: (value: number) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customDatasetOptions?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customGridOptions?: any;
  hideTooltip?: boolean;
  thinLines?: boolean;
  /**
   * When true, the chart scrolls the marker (e.g. the "FORECAST FROM" line) into the centre of its
   * scroll container once it has been positioned. Used by the invoices cash position chart so it
   * opens centred on the current date when previous actuals are displayed.
   */
  autoScrollToMarker?: boolean;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Filler);
ChartJS.defaults.color = '#7C879D';
ChartJS.defaults.font = {
  family: 'SageUI',
  size: 10,
  weight: '500'
};
ChartJS.defaults.plugins.tooltip.backgroundColor = 'rgb(92, 101, 120)';

export const UILineChart: FC<Props> = ({
  ariaLabel,
  height,
  legend,
  title,
  labels,
  datasets,
  testId,
  absolute,
  markerIndex,
  showMarker,
  showYesterdayMarker,
  lineColorFn,
  areaColorFn,
  dateFormatter,
  currencyFormatter,
  customDatasetOptions,
  customGridOptions,
  hideTooltip,
  thinLines,
  autoScrollToMarker = false
}: Props) => {
  const [markerX, setMarkerX] = useState<number | undefined>();
  const [markerY, setMarkerY] = useState<number | undefined>();
  const [markerH, setMarkerH] = useState<number>(15);
  const chartRef = useRef(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const autoScrolledMarkerIndexRef = useRef<number | undefined>(undefined);
  const { tableRef } = useContext(DataGridContext);

  const intl = useIntl();
  const { forecast, viewType } = usePredictionsInfoContext();

  const isPaymentsView = viewType === VIEW_TYPES.CASH_FLOW;

  // Once the marker has a position, bring it into the centre of the grid's own scroll container.
  // We deliberately avoid `scrollIntoView` here: with the sticky/full-bleed layout used around this
  // chart, the browser can resolve the "nearest scrollable ancestor" to something other than the
  // grid's horizontal scroll container (e.g. the document itself), which can leave the page shifted
  // with no way to scroll back. Setting `scrollLeft` directly on the known grid container avoids that.
  useEffect(() => {
    if (!autoScrollToMarker || markerX === undefined || markerY === undefined) {
      return;
    }
    if (autoScrolledMarkerIndexRef.current === markerIndex || !markerRef.current) {
      return;
    }
    const scrollContainer = tableRef?.current;
    if (!scrollContainer) {
      return;
    }
    // Defer to the next frame so this runs after any pending layout/resize recalculation
    // (e.g. the data grid's own ResizeObserver-driven width measurement) has settled.
    const frameId = window.requestAnimationFrame(() => {
      if (!markerRef.current || !scrollContainer) {
        return;
      }
      const markerRect = markerRef.current.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const markerCenter = markerRect.left - containerRect.left + scrollContainer.scrollLeft + markerRect.width / 2;
      const targetScrollLeft = markerCenter - scrollContainer.clientWidth / 2;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.scrollLeft = Math.min(Math.max(targetScrollLeft, 0), Math.max(maxScrollLeft, 0));
    });
    autoScrolledMarkerIndexRef.current = markerIndex;
    // eslint-disable-next-line consistent-return
    return () => window.cancelAnimationFrame(frameId);
  }, [autoScrollToMarker, markerX, markerY, markerIndex, tableRef]);

  const updateMarkerPosition = () => {
    if ((showYesterdayMarker || showMarker) && chartRef.current && markerIndex && markerRef.current) {
      const ctx = (chartRef.current as unknown) as {
        scales: { y: { ticks: { value: number }[]; _gridLineItems: { y1: number }[] } };
        _metasets: { data: { x: number; y: number }[] }[];
      };
      if (ctx._metasets.length) {
        const x =
          (ctx._metasets[0].data[markerIndex]?.x || 0) -
          1 -
          (showYesterdayMarker ? markerRef.current?.clientWidth || 0 : 0) / 2;
        let h = 15,
          y = ctx._metasets[0].data[markerIndex].y - h - 18;
        if ((datasets[0].data[markerIndex] || 0) < 0) {
          const zeroTickIndex = ctx.scales.y.ticks.findIndex(t => t.value === 0);
          if (zeroTickIndex !== -1 && ctx.scales.y._gridLineItems) {
            const zeroTickPos = ctx.scales.y._gridLineItems[zeroTickIndex]?.y1;
            h = Math.abs(ctx._metasets[0].data[markerIndex].y - zeroTickPos - 4);
            y = ctx._metasets[0].data[markerIndex].y - h - 18;
          }
        } else if (!showYesterdayMarker) {
          const zeroTickIndex = ctx.scales.y.ticks.findIndex(t => t.value === 0);
          if (zeroTickIndex !== -1 && ctx.scales.y._gridLineItems) {
            const zeroTickPos = ctx.scales.y._gridLineItems[zeroTickIndex]?.y1;
            h = Math.abs(ctx._metasets[0].data[markerIndex].y - zeroTickPos);
            h = h > 4 ? h - 4 : 0;
            y = ctx._metasets[0].data[markerIndex].y + 4;
          }
        }
        setMarkerX(x);
        setMarkerY(y);
        setMarkerH(h);
      }
    }
  };

  const options = useMemo(
    () => ({
      responsive: true,
      devicePixelRatio: 2,
      maintainAspectRatio: false,
      plugins: {
        legendMargin: false,
        legend: {
          display: !!legend,
          position: legend || 'top'
        },
        title: {
          display: !!title,
          text: title || ''
        },
        tooltip: hideTooltip
          ? { enabled: false }
          : {
              displayColors: false,
              callbacks: {
                title: dateFormatter
                  ? (tooltipItems: TooltipItem<'line'>[]) =>
                      dateFormatter(tooltipItems[0].label, tooltipItems[0].formattedValue)
                  : undefined,
                label: currencyFormatter
                  ? (ctx: TooltipItem<'line'>) => currencyFormatter(ctx.raw as number)
                  : undefined
              }
            },
        datalabels: {
          display: false
        }
      },
      interaction: {
        intersect: false,
        // eslint-disable-next-line @typescript-eslint/prefer-as-const
        mode: 'index' as 'index'
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false
          },
          ticks: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            drawBorder: false,
            drawTicks: false,
            borderDash: [2, 2],
            color: (ctx: { tick: { value: number } }) => (ctx.tick.value === 0 ? '#9196A4' : 'rgba(0, 0, 0, 0)'),
            ...customGridOptions
          },
          ticks: {
            display: false
          }
        }
      },
      elements: {
        point: {
          radius: (ctx: { dataIndex: number }) => (ctx.dataIndex === markerIndex ? 4 : 0)
        }
      },
      animation: {
        onComplete: () => {
          updateMarkerPosition();
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, legend, markerIndex]
  );

  const lineData = {
    labels: labels.map(l => (typeof l === 'string' && l.length > 30 ? `${l.substring(0, 27)}...` : l)),
    datasets: datasets.map((ds, i) => ({
      id: i,
      label: ds.label || '',
      data: ds.data,
      tension: 0.1,
      borderWidth: thinLines ? 1 : 2,
      fill: true,
      pointBackgroundColor: '#7C879D',
      segment: lineColorFn
        ? {
            borderColor: lineColorFn,
            backgroundColor: areaColorFn
          }
        : undefined,
      ...customDatasetOptions
    }))
  };

  return (
    <div
      className={clsx([styles.chartWrapper, absolute && styles.absolute])}
      style={{ height: height || '' }}
      data-testid={testId || 'bar-chart'}
    >
      <Line
        options={options}
        data={lineData}
        ref={chartRef}
        aria-label={
          ariaLabel ||
          intl.formatMessage({
            id: 'predict.chart.line_chart.label',
            defaultMessage: 'Line chart'
          })
        }
      />
      {(showMarker || showYesterdayMarker) && !isPaymentsView && (
        <div
          className={clsx(styles.marker, 'uiTypeFont10MediumLightUpper')}
          style={{
            left: markerX,
            top: markerY,
            visibility: markerX !== undefined && markerY !== undefined ? 'visible' : 'hidden'
          }}
          ref={markerRef}
        >
          {showYesterdayMarker && forecast?.kind === 'live' && (
            <span className={clsx(styles.markerText)}>
              <FormattedMessage id="predict.last_sync_capital" defaultMessage="LAST SYNC" />
            </span>
          )}
          {showYesterdayMarker && forecast?.kind === 'scenario' && (
            <span className={clsx(styles.markerText)}>
              <FormattedMessage id="predict.forecast_from_capital" defaultMessage="FORECAST FROM" />
            </span>
          )}
          {forecast?.kind !== 'budget' && (
            <span
              className={clsx(styles.markerStick)}
              style={{ height: markerH, top: showYesterdayMarker ? 14 : 0 }}
            ></span>
          )}
        </div>
      )}
    </div>
  );
};
